import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Upload, X, ImageIcon } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface GymLogoUploadProps {
  currentLogoUrl?: string;
  onLogoChange: (file: File | null) => void;
  isEditing: boolean;
}

export const GymLogoUpload = ({
  currentLogoUrl,
  onLogoChange,
  isEditing,
}: GymLogoUploadProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    currentLogoUrl || null
  );

  // Sincronizar previewUrl cuando currentLogoUrl cambie (después de guardar)
  useEffect(() => {
    setPreviewUrl(currentLogoUrl || null);
  }, [currentLogoUrl]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/)) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Solo se permiten imágenes JPG, PNG o WEBP",
      });
      return;
    }

    // Validar tamaño (2MB máximo)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "El archivo no debe superar los 2MB",
      });
      return;
    }

    // Crear preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);

    onLogoChange(file);
  };

  const handleRemoveLogo = () => {
    setPreviewUrl(null);
    onLogoChange(null);
  };

  if (!isEditing && !currentLogoUrl) return null;

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2 text-muted-foreground">
        <ImageIcon className="h-4 w-4" />
        Logo del Gimnasio
      </Label>
      
      {previewUrl ? (
        <div className="relative inline-block group">
          <div className="absolute -inset-1 rounded-xl bg-gradient-to-br from-primary/30 to-primary/10 opacity-75" />
          <div className="absolute -inset-0.5 rounded-lg bg-background" />
          <img
            src={previewUrl}
            alt="Logo del gimnasio"
            className="relative h-24 w-24 object-contain rounded-lg border border-border/50"
          />
          {isEditing && (
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute -top-2 -right-2 h-6 w-6 shadow-sm"
              onClick={handleRemoveLogo}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ) : (
        isEditing && (
          <div 
            className="flex flex-col items-center justify-center gap-3 p-6 rounded-xl border-2 border-dashed border-border/50 hover:border-primary/50 bg-background/50 hover:bg-background/80 transition-all cursor-pointer group"
            onClick={() => document.getElementById("gym-logo")?.click()}
          >
            <input
              type="file"
              id="gym-logo"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFileChange}
              className="hidden"
            />
            <div className="p-3 rounded-full bg-muted group-hover:bg-primary/10 transition-colors">
              <Upload className="h-6 w-6 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-foreground">Subir Logo</p>
              <p className="text-xs text-muted-foreground">
                JPG, PNG o WEBP (máx. 2MB)
              </p>
            </div>
          </div>
        )
      )}
    </div>
  );
};
